import { ArrowLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';

import { AppsSection } from '@/components/settings/sections/AppsSection';
import { BackupSection } from '@/components/settings/sections/BackupSection';
import { EinbuergerungstestSection } from '@/components/settings/sections/EinbuergerungstestSection';
import { ImportSection } from '@/components/settings/sections/ImportSection';
import { LanguagesSection } from '@/components/settings/sections/LanguagesSection';
import { PauseSection } from '@/components/settings/sections/PauseSection';
import { StreakSection } from '@/components/settings/sections/StreakSection';
import { TimezoneSection } from '@/components/settings/sections/TimezoneSection';
import { Button } from '@/components/ui/button';
import { SettingsSkeleton } from '@/components/ui/skeletons';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useApps, useSettings } from '@/lib/queries';

const TABS = [
  { id: 'streak', label: 'Streak' },
  { id: 'languages', label: 'Languages' },
  { id: 'timezone', label: 'Time zone' },
  { id: 'apps', label: 'Apps' },
  { id: 'einbuergerungstest', label: 'Einbürgerungstest' },
  { id: 'import', label: 'Import' },
  { id: 'backup', label: 'Backup' },
] as const;

export function SettingsRoute() {
  const settings = useSettings();
  const appsQuery = useApps();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'streak';

  const handleTab = (id: string) => {
    setSearchParams({ tab: id });
  };

  return (
    <div className="min-h-screen">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
          <h1 className="font-display text-xl font-semibold">Settings</h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
        <Tabs value={activeTab} onValueChange={handleTab}>
          <div className="overflow-x-auto">
            <TabsList>
              {TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="mt-6">
            {(settings.isLoading || appsQuery.isLoading) && <SettingsSkeleton />}
            {(settings.error || appsQuery.error) && (
              <p className="text-destructive text-sm">
                {(settings.error ?? appsQuery.error)?.message}
              </p>
            )}
            {settings.data && appsQuery.data && (
              <>
                <TabsContent value="streak">
                  {/* Vacation first: it is the thing you come here in a hurry
                      to do, and it saves on its own rather than with the form. */}
                  <div className="flex flex-col gap-8">
                    <PauseSection />
                    <div className="border-border border-t pt-8">
                      <StreakSection settings={settings.data} apps={appsQuery.data.apps} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="languages">
                  <LanguagesSection settings={settings.data} />
                </TabsContent>
                <TabsContent value="timezone">
                  <TimezoneSection settings={settings.data} />
                </TabsContent>
                <TabsContent value="apps">
                  <AppsSection settings={settings.data} apps={appsQuery.data.apps} />
                </TabsContent>
                <TabsContent value="einbuergerungstest">
                  <EinbuergerungstestSection />
                </TabsContent>
                <TabsContent value="import">
                  <ImportSection apps={appsQuery.data.apps} />
                </TabsContent>
                <TabsContent value="backup">
                  <BackupSection />
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>
      </main>
    </div>
  );
}
